import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { GroupProduct } from '@meitu/subscribe-core';
import {
  PurchaseScene,
  SubscribeType,
  isTeamSubscription,
  purchaseScene,
  subscribeType,
} from '@/constants';
import { addParamsToUrl, getUrlParam, showToast } from '@/utils';
import { BusinessType, PromotionType, TabType } from '@/constants/subscription';
import { getDisplayPrice } from '@/utils/vip';
import { useAtom } from 'jotai';
import {
  businessTypeAtom,
  curProductAtom,
  curTabAtom,
  loadingAtom,
  seatsAtom,
  teamInfoAtom,
  vipInfoAtom,
  visibleBusinessAtom,
} from '@/store';
import { track } from '@/utils/common';
import { useLocaleStore } from '@/i18n/localeStore';

import styles from './index.module.css';

/** 加购场景团队席位总数上限（加购上限 = 该值 - 当前有效席位数） */
const maxTeamSeats = 10;
const minNumExtra = 1;

/** 团队会员等级对应每月赠送美豆数 */
const TEAM_LEVEL_MEIDOU_MAP: Record<number, number> = {
  1: 330,
  2: 1800,
  3: 7500,
};

const Seats = () => {
  const { $t } = useLocaleStore();
  const [visible, setVisible] = useState<boolean>(false);
  const [val, setVal] = useState<number>();
  const timer = useRef<any>();
  const moduleRef = useRef<any>();

  const [loading] = useAtom(loadingAtom);
  const [curTab] = useAtom(curTabAtom);
  const [curProduct] = useAtom(curProductAtom);
  const [teamInfo] = useAtom(teamInfoAtom);
  const [vipInfo] = useAtom(vipInfoAtom);
  const [seats, setSeats] = useAtom(seatsAtom);
  const [, setVisibleBusiness] = useAtom(visibleBusinessAtom);
  const [, setBusinessType] = useAtom(businessTypeAtom);

  const upper = (curProduct as GroupProduct)?.seatInfo?.upperLimit;
  const userCount = teamInfo?.member_detail?.user_count || 0;
  // 商品最低购买席位数
  const productLower = (curProduct as GroupProduct)?.seatInfo?.lowerLimit || 0;
  const lower =
    purchaseScene === PurchaseScene.EXTRA ? minNumExtra : Math.max(userCount, productLower);
  // 加购上限 = 10 - 当前有效席位数
  const validSeatNum = teamInfo?.member_detail?.valid_num || 0;
  const maxNumExtra = Math.max(0, maxTeamSeats - validSeatNum);

  const handleChange = (num: number) => {
    setSeats(num);
    setVal(num);
  };

  const handleInputChange = (e: any) => {
    if (loading) return;

    if (timer.current) {
      clearTimeout(timer.current);
    }

    let val = Math.floor(+e.target.value || 0);

    setVal(val);

    timer.current = setTimeout(() => {
      if (purchaseScene === PurchaseScene.NORMAL && lower > val) {
        handleChange(lower);
      } else if (purchaseScene === PurchaseScene.EXTRA && minNumExtra > val) {
        handleChange(minNumExtra);
      } else if (purchaseScene === PurchaseScene.NORMAL && upper !== undefined && upper < val) {
        handleChange(upper);
      } else if (purchaseScene === PurchaseScene.EXTRA && val > maxNumExtra) {
        handleChange(maxNumExtra);
      } else {
        handleChange(val);
      }
    }, 1000);
  };

  const handleReduce = () => {
    if (loading) return;

    if (
      (purchaseScene === PurchaseScene.NORMAL && lower < seats) ||
      (purchaseScene === PurchaseScene.EXTRA && seats > minNumExtra)
    ) {
      handleChange(seats - 1);
      const params = {
        class: 'reduce',
        name_id: curProduct?.productId,
      };
      track({
        event_id: 'vip_page_click',
        event_name: '会员落地页点击',
        params,
      });
    } else {
      if (purchaseScene === PurchaseScene.NORMAL) {
        const tips =
          userCount >= productLower
            ? $t((v, { r }) => r(v.Text_TeamSeatRequirement, [userCount]))
            : $t((v, { r }) => r(v.Text_TeamMemberSeatMinimum, [productLower]));
        showToast(tips, moduleRef.current);
      }
    }
  };

  const handleIncrease = () => {
    if (loading) return;

    if (
      (purchaseScene === PurchaseScene.NORMAL && (upper === undefined || seats < upper)) ||
      (purchaseScene === PurchaseScene.EXTRA && seats < maxNumExtra)
    ) {
      handleChange(seats + 1);

      const params = {
        class: 'add',
        name_id: curProduct?.productId,
      };
      track({
        event_id: 'vip_page_click',
        event_name: '会员落地页点击',
        params,
      });
    } else {
      setBusinessType(BusinessType.LargeSeats);
      setVisibleBusiness(true);
    }
  };

  const handleJump = () => {
    const params = {
      class: 'installment',
      name_id: curProduct?.productId,
    };
    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params,
    });

    setTimeout(() => {
      const extraUrl = addParamsToUrl(window.location.href, 'purchase_scene', PurchaseScene.EXTRA);
      const newUrl = addParamsToUrl(extraUrl, 'subscribe_type', SubscribeType.SUBSCRIPTION);
      window.history.pushState(null, '', newUrl);
      window.location.reload();
    }, 300);
  };

  useEffect(() => {
    setVal(seats);
  }, [seats]);

  useEffect(() => {
    // 会员失效团队场景不展示
    if (!vipInfo?.is_vip) {
      return;
    }
    // 有效的席位数
    // 续费时，购买会员且有生效席位数
    if (purchaseScene === PurchaseScene.NORMAL && teamInfo?.member_detail?.operable_num) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [val]);

  useEffect(() => {
    const defaultSeats = +getUrlParam('seats');

    if (!teamInfo || !defaultSeats) return;

    if (userCount > defaultSeats) {
      const tips = $t((v, { r }) => r(v.Text_TeamSeatRequirement, [userCount]));
      showToast(tips, moduleRef.current);
      return;
    }
  }, [teamInfo]);

  // 只有团队版的购买会员才有加购模块
  if (!(isTeamSubscription && curTab === TabType.SUB)) {
    return null;
  }

  // 取第一个优惠信息
  const promotionInfo: any = curProduct?.promotions && curProduct.promotions[0];
  // 是否是阶梯优惠商品类型
  const isTieredDiscounts = promotionInfo?.promotionType === PromotionType.TieredDiscounts;

  const showSkeleton = !curProduct;

  const renderText = () => {
    if (isTieredDiscounts) {
      return (
        <span
          className={styles.desc}
          dangerouslySetInnerHTML={{
            __html: $t((v, { r }) =>
              r(v.Text_TieredSeatBilling, [
                promotionInfo.enjoyNum,
                promotionInfo.enjoyNum,
                `<span className="${styles.highlight}">${
                  +(curProduct?.productPrice.price || 0) / 100
                }${$t((v) => v.Text_PerYearPerPerson)}</span>`,
              ])
            ),
          }}
        ></span>
      );
    }

    // 加购，购买会员(非续期)
    if (purchaseScene === PurchaseScene.EXTRA) return null;

    if (!curProduct?.isProbation) {
      const level = (curProduct as any)?.subCommodityBo?.level;
      const meidouPerSeat = TEAM_LEVEL_MEIDOU_MAP[level] ?? 0;
      return (
        <span
          className={styles.desc}
          style={{
            fontSize: 14,
          }}
        >
          {/* {userCount >= productLower
            ? `当前人数：${userCount}`
            : vipInfo?.is_vip
            ? `${productLower}个起购`
            : ''} */}
          {$t((v, { r }) => r(v.Text_MeidouGiftPerMonth, [meidouPerSeat * seats]))}
        </span>
      );
    }

    return null;
  };

  return (
    <div className={styles.module} ref={moduleRef}>
      <div className={styles.body}>
        <div className={classnames(styles.row, showSkeleton && styles.skeleton)}>
          <span>{$t((v) => v.Label_SeatCount)}</span>
        </div>
        <div className={classnames(styles.row, showSkeleton && styles.skeleton)}>
          <span
            className={classnames(
              styles.btn,
              ((purchaseScene === PurchaseScene.NORMAL && lower >= seats) ||
                (purchaseScene === PurchaseScene.EXTRA && seats <= minNumExtra)) &&
                styles.disabled
            )}
            onClick={handleReduce}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
            >
              <path
                d="M4.2627 10.9999C4.2627 10.3924 4.75518 9.8999 5.3627 9.8999H16.6377C17.2452 9.8999 17.7377 10.3924 17.7377 10.9999C17.7377 11.6074 17.2452 12.0999 16.6377 12.0999H5.3627C4.75518 12.0999 4.2627 11.6074 4.2627 10.9999Z"
                fill="#0F0F0F"
              />
            </svg>
          </span>
          <div className={styles.inputBox}>
            <input
              type="number"
              className={classnames(styles.input)}
              value={val}
              onChange={handleInputChange}
              disabled={curProduct?.isProbation}
            />
            {/* <span>人</span> */}
          </div>
          <span
            className={classnames(
              styles.btn
              // ((purchaseScene === PurchaseScene.NORMAL && upper !== undefined && upper <= seats) ||
              //   (purchaseScene === PurchaseScene.EXTRA && seats >= maxNumExtra)) &&
              //   styles.disabled
            )}
            onClick={handleIncrease}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
            >
              <path
                d="M9.9001 16.3626C9.9001 16.9701 10.3926 17.4626 11.0001 17.4626C11.6076 17.4626 12.1001 16.9701 12.1001 16.3626V12.1001H16.3626C16.9701 12.1001 17.4626 11.6076 17.4626 11.0001C17.4626 10.3926 16.9701 9.9001 16.3626 9.9001H12.1001V5.6376C12.1001 5.03008 11.6076 4.5376 11.0001 4.5376C10.3926 4.5376 9.9001 5.03008 9.9001 5.6376V9.9001H5.6376C5.03008 9.9001 4.5376 10.3926 4.5376 11.0001C4.5376 11.6076 5.03008 12.1001 5.6376 12.1001H9.9001V16.3626Z"
                fill="#0F0F0F"
              />
            </svg>
          </span>
        </div>
        <div className={classnames(styles.row, showSkeleton && styles.skeleton)}>
          {renderText()}
        </div>
      </div>
      {visible ? (
        <div className={styles.tips}>
          <span className={styles.row}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M0.583328 6.9987C0.583328 3.45487 3.45617 0.582031 7 0.582031C10.5438 0.582031 13.4167 3.45487 13.4167 6.9987C13.4167 10.5425 10.5438 13.4154 7 13.4154C3.45617 13.4154 0.583328 10.5425 0.583328 6.9987ZM7.58333 6.5612C7.58333 6.23903 7.32216 5.97786 7 5.97786C6.67783 5.97786 6.41666 6.23903 6.41666 6.5612V9.91537C6.41666 10.2375 6.67783 10.4987 7 10.4987C7.32216 10.4987 7.58333 10.2375 7.58333 9.91537V6.5612ZM7 3.64453C6.55702 3.64453 6.19791 4.00364 6.19791 4.44661C6.19791 4.88959 6.55702 5.2487 7 5.2487C7.44297 5.2487 7.80208 4.88959 7.80208 4.44661C7.80208 4.00364 7.44297 3.64453 7 3.64453Z"
                fill="#939599"
              />
            </svg>
            {/* 续期订单生效时间逻辑:当前的会员等级 < 商品配置的会员等级 => 立马生效; 平级或者降级购买 => 当前有效期结束后生效 */}
            {$t((v, { r }) => {
              const currentLevel = teamInfo?.vip_level ?? vipInfo?.vip_level ?? 0;
              const productLevel = (curProduct as any)?.subCommodityBo?.level ?? 0;
              const vipEndTime = vipInfo?.vip_end_time;
              const effectDate =
                currentLevel < productLevel
                  ? dayjs().format('YYYY-MM-DD')
                  : vipEndTime
                  ? dayjs(vipEndTime * 1000)
                      .add(1, 'day')
                      .format('YYYY-MM-DD')
                  : '';
              return r(v.Text_SeatOrderEffect, [effectDate]);
            })}
          </span>
          <span className={styles.link} onClick={handleJump}>
            {$t((v) => v.Button_AddSeat)}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(Seats);
